import { object, string, array } from 'yup';
import { WorkOrder } from '../../../types/workOrder';
import { pkValidation } from '../../../utils/pkValidation';
import i18n from '../../../i18n';
import { PK_FORMAT_REGEX } from '../../../utils/transform';

export const createWorkOrderSchema = (
  directionRequired: boolean,
  marginShown = true,
) => object<WorkOrder>({
  operator: string().required(() => i18n.t('validation:required')),
  registerInitDate: string(),
  actionActivity: string().when('$isAction', ([isAction], schema) =>
    isAction
      ? schema.required(() => i18n.t('validation:required'))
      : schema.notRequired()
  ),
  actionElementOrder: string().when('$isAction', ([isAction], schema) =>
    isAction
      ? schema.required(() => i18n.t('validation:required'))
      : schema.notRequired()
  ),
  // Operario que registra: obligatorio solo al CREAR Actuación/Accidente
  // (contexto `registeredByRequired`). En edición/otros flujos → opcional.
  registeredByUserId: string().when(
    '$registeredByRequired',
    ([registeredByRequired], schema) =>
      registeredByRequired
        ? schema.required(() => i18n.t('validation:required'))
        : schema.notRequired()
  ),
  predefine: string().required(() => i18n.t('validation:required')),
  capitol: string().required(() => i18n.t('validation:required')),
  subCapitol: string().required(() => i18n.t('validation:required')),
  classification: string().when(
    '$isActionOrIsAccident',
    ([isActionOrIsAccident], schema) =>
      isActionOrIsAccident
        ? schema.notRequired()
        : schema.required(() => i18n.t('validation:required'))
  ),
  concession: string().required(() => i18n.t('validation:required')),
  city: string().required(() => i18n.t('validation:required')),
  pkInitRoad: string().required(() => i18n.t('validation:required')),
  pkEndRoad: string().required(() => i18n.t('validation:required')),
  road: string().required(() => i18n.t('validation:required')),
  pkInit: string()
    .matches(PK_FORMAT_REGEX, () => i18n.t('validation:pk.format'))
    .test('pk-validation', function (value) {
      if (PK_FORMAT_REGEX.test(value || '') && pkValidation(value || '', this.parent)) {
        return true;
      }
      return this.createError({ message: i18n.t('validation:pk.validation') });
    })
    .required(() => i18n.t('validation:required')),
  pkEnd: string()
    .matches(PK_FORMAT_REGEX, () => i18n.t('validation:pk.format'))
    .test('pk-validation', function (value) {
      if (PK_FORMAT_REGEX.test(value || '') && pkValidation(value || '', this.parent)) {
        return true;
      }
      return this.createError({ message: i18n.t('validation:pk.validation') });
    })
    .required(() => i18n.t('validation:required')),
  track: string().required(() => i18n.t('validation:required')),
  direction: directionRequired
    ? string().required(() => i18n.t('validation:required'))
    : string().nullable().optional(),
  // Fix 2026-07-04: margin solo puede ser obligatorio si su campo se MUESTRA
  // (flag showMargin). Con el flag apagado el form de actuación quedaba
  // imposible de enviar: requerido + invisible + submit silencioso.
  margin: string().when('$isAction', ([isAction], schema) =>
    isAction && marginShown
      ? schema.required(() => i18n.t('validation:required'))
      : schema.notRequired()
  ),
  lane: string().required(() => i18n.t('validation:required')),
  campaign: string().when('$switchState', ([switchState], schema) =>
    switchState
      ? schema.required(() => i18n.t('validation:required'))
      : schema.notRequired()
  ),
  isCampaignActive: string(),
  description: string().required(() => i18n.t('validation:required')),
  notes: string().notRequired(),
  teamRelationships: array()
    .of(string())
    .when('$isActionOrIsAccident', ([isActionOrIsAccident], schema) =>
      isActionOrIsAccident
        ? schema.notRequired()
        : schema
            // `.min(1)` por sí solo NO falla con `undefined` (solo con `[]`); al
            // crear, el valor inicial es undefined → sin `.required()` no marcaba
            // el equipo como obligatorio. Ambos juntos cubren undefined y [].
            .required(() => i18n.t('validation:required'))
            .min(1, () => i18n.t('validation:required')),
    ),
});

// Backward compat: default schema con direction required
export const newWorkOrderSchema = createWorkOrderSchema(true);
